const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

class ApiService {
  constructor() {
    this.baseUrl = API_BASE_URL;
    this.lastRequest = null;
  }

  setLastRequest(method, endpoint) {
    this.lastRequest = { method, endpoint, timestamp: Date.now() };
  }

  async getAuthHeaders() {
    const token = localStorage.getItem("auth_token");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  async handleResponse(response) {
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "API request failed");
    }
    return response.json();
  }

  async post(endpoint, data) {
    try {
      this.setLastRequest("POST", endpoint);
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  }

  async get(endpoint) {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        if (response.status === 429) {
          // Rate limit hit - wait and retry
          await new Promise((resolve) => setTimeout(resolve, 2000));
          return this.get(endpoint);
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  }

  // AI Chat
  async chat(messages, noteTitle = "", noteContent = "") {
    return this.post("/api/ai/chat", {
      messages,
      noteTitle,
      noteContent,
    });
  }

  // Notes
  async getNotes() {
    return this.get("/api/notes");
  }

  async createNote(note) {
    return this.post("/api/notes", note);
  }

  async updateNote(id, note) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${this.baseUrl}/api/notes/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(note),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error("Server error:", error);
        throw new Error("Failed to update note");
      }

      return response.json();
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  }

  // Tests
  async createTest(testData) {
    return this.post("/api/tests", testData);
  }

  async getTests() {
    return this.get("/api/tests");
  }

  async getTestResults() {
    return this.get("/api/test-results");
  }

  // Add if not already present
  async patch(endpoint, data) {
    try {
      this.setLastRequest("PATCH", endpoint, data);
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(data),
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  }

  async delete(endpoint) {
    try {
      this.setLastRequest("DELETE", endpoint);
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: "DELETE",
        headers,
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  }

  async generateMindMap(content) {
    try {
      const response = await this.post("/api/ai/generate-mindmap", {
        content,
      });
      return response;
    } catch (error) {
      console.error("Mind map generation error:", error);
      throw error;
    }
  }

  async summarizeYouTube(url) {
    return this.post("/api/ai/summarize-youtube", { url });
  }
}

export const apiService = new ApiService();
